import { useState, useMemo } from "react";
import { Card } from "../ui/Card";
import { TableActionMenu } from "../ui/TableActionMenu";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import {
  Search,
  Filter,
  ArrowUpDown,
  MoreHorizontal,
  Edit2,
  Trash2,
  CheckCircle2,
  XCircle,
  Shield,
} from "lucide-react";
import { User as Admin } from "../../services/userService";
import { useAuth } from "../../contexts/AuthContext";

interface AdminTableProps {
  admins: Admin[];
  description?: string;
  isLoading?: boolean;
  onAddClick?: () => void;
  onEditClick: (admin: Admin) => void;
  onDeleteClick: (admin: Admin) => void;
  extraHeaderActions?: React.ReactNode;
}

type SortField = "firstName" | "lastName" | "email" | "createdAt" | "updatedAt";
type SortOrder = "asc" | "desc";

export function AdminTable({
  admins,
  description = "Manage your administrators and their system access.",
  isLoading,
  onAddClick,
  onEditClick,
  onDeleteClick,
  extraHeaderActions,
}: AdminTableProps) {
  const { hasPermission } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState<SortField>("createdAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [statusFilter, setStatusFilter] = useState<
    "all" | "verified" | "unverified"
  >("all");
  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
  };

  const filteredAndSortedAdmins = useMemo(() => {
    return admins
      .filter((admin) => {
        const matchesSearch =
          (admin.firstName?.toLowerCase() || "").includes(
            searchTerm.toLowerCase(),
          ) ||
          (admin.lastName?.toLowerCase() || "").includes(
            searchTerm.toLowerCase(),
          ) ||
          admin.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (admin.username?.toLowerCase() || "").includes(
            searchTerm.toLowerCase(),
          );

        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "verified" && admin.isEmailVerified) ||
          (statusFilter === "unverified" && !admin.isEmailVerified);

        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        let valA: any = a[sortField] || "";
        let valB: any = b[sortField] || "";

        if (sortField === "createdAt" || sortField === "updatedAt") {
          valA = new Date(valA).getTime();
          valB = new Date(valB).getTime();
        } else if (typeof valA === "string") {
          valA = valA.toLowerCase();
          valB = (valB as string).toLowerCase();
        }

        if (valA < valB) return sortOrder === "asc" ? -1 : 1;
        if (valA > valB) return sortOrder === "asc" ? 1 : -1;
        return 0;
      });
  }, [admins, searchTerm, sortField, sortOrder, statusFilter]);

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">
          Admins
        </h2>
        <p className="text-gray-500">{description}</p>
      </div>

      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        {/* Search input */}
        <div className="w-full sm:w-[408px]">
          <Input
            placeholder="Search admins"
            icon={<Search size={20} />}
            className="bg-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Filter buttons and primary action */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full xl:w-auto">
          <div className="grid grid-cols-2 sm:flex items-center gap-2 w-full sm:w-auto">
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto justify-center touch-target"
                onClick={() => {
                  setIsFilterOpen(!isFilterOpen);
                  setIsSortOpen(false);
                }}
              >
                <Filter size={16} /> Filter
              </Button>
              {isFilterOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsFilterOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[120px] animate-in fade-in zoom-in duration-150">
                    <button
                      onClick={() => {
                        setStatusFilter("all");
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        statusFilter === "all" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      All Status
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter("verified");
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        statusFilter === "verified"
                          ? "bg-gray-50 font-bold text-green-600"
                          : ""
                      }`}
                    >
                      Verified
                    </button>
                    <button
                      onClick={() => {
                        setStatusFilter("unverified");
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        statusFilter === "unverified"
                          ? "bg-gray-50 font-bold text-red-600"
                          : ""
                      }`}
                    >
                      Unverified
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto justify-center touch-target"
                onClick={() => {
                  setIsSortOpen(!isSortOpen);
                  setIsFilterOpen(false);
                }}
              >
                <ArrowUpDown size={16} /> Sort By
              </Button>
              {isSortOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsSortOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[150px] animate-in fade-in zoom-in duration-150">
                    <button
                      onClick={() => {
                        handleSort("createdAt");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "createdAt" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      Registration Date
                    </button>
                    <button
                      onClick={() => {
                        handleSort("firstName");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "firstName" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      First Name
                    </button>
                    <button
                      onClick={() => {
                        handleSort("email");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "email" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      Email
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Primary button and extra actions */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            {extraHeaderActions}
            {hasPermission('admin:create') && (
              <Button
                variant="primary"
                size="lg"
                className="w-full sm:w-[208px] whitespace-nowrap h-[48px] sm:h-[44px] touch-target"
                onClick={onAddClick}
              >
                Add new admin
              </Button>
            )}
          </div>
        </div>
      </div>

      <div>
        <Card className="p-0 border shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-[1000px] w-full text-left text-sm">
              <thead className="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th
                    className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[15%] cursor-pointer hover:text-indigo-600 transition-colors"
                    onClick={() => handleSort("firstName")}
                  >
                    First name{" "}
                    <ArrowUpDown
                      size={14}
                      className={`inline ml-1 ${
                        sortField === "firstName"
                          ? "text-indigo-600"
                          : "text-gray-400"
                      }`}
                    />
                  </th>
                  <th
                    className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[15%] cursor-pointer hover:text-indigo-600 transition-colors"
                    onClick={() => handleSort("lastName")}
                  >
                    Last name{" "}
                    <ArrowUpDown
                      size={14}
                      className={`inline ml-1 ${
                        sortField === "lastName"
                          ? "text-indigo-600"
                          : "text-gray-400"
                      }`}
                    />
                  </th>
                  <th
                    className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[20%] cursor-pointer hover:text-indigo-600 transition-colors"
                    onClick={() => handleSort("email")}
                  >
                    Email{" "}
                    <ArrowUpDown
                      size={14}
                      className={`inline ml-1 ${
                        sortField === "email"
                          ? "text-indigo-600"
                          : "text-gray-400"
                      }`}
                    />
                  </th>
                  <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[15%]">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[12%]">
                    Role Type
                  </th>
                  <th className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[12%]">
                    Permissions
                  </th>
                  <th
                    className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[15%] cursor-pointer hover:text-indigo-600 transition-colors"
                    onClick={() => handleSort("createdAt")}
                  >
                    Registration Date{" "}
                    <ArrowUpDown
                      size={14}
                      className={`inline ml-1 ${
                        sortField === "createdAt"
                          ? "text-indigo-600"
                          : "text-gray-400"
                      }`}
                    />
                  </th>
                  <th
                    className="px-3 sm:px-6 py-4 font-semibold text-gray-900 w-[15%] cursor-pointer hover:text-indigo-600 transition-colors"
                    onClick={() => handleSort("updatedAt")}
                  >
                    Last Login{" "}
                    <ArrowUpDown
                      size={14}
                      className={`inline ml-1 ${
                        sortField === "updatedAt"
                          ? "text-indigo-600"
                          : "text-gray-400"
                      }`}
                    />
                  </th>
                  <th className="px-3 sm:px-6 py-4 w-[5%]"></th>
                </tr>
              </thead>
               <tbody className="divide-y divide-gray-100">
                {isLoading ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-3 sm:px-6 py-8 text-center text-gray-500"
                    >
                      Loading admins...
                    </td>
                  </tr>
                ) : filteredAndSortedAdmins.length === 0 ? (
                  <tr>
                    <td
                      colSpan={9}
                      className="px-3 sm:px-6 py-8 text-center text-gray-500"
                    >
                      No admins found
                    </td>
                  </tr>
                ) : (
                  filteredAndSortedAdmins.map((admin) => (
                    <tr
                      key={admin._id}
                      className="hover:bg-gray-50/50 transition-colors"
                    >
                      <td className="px-3 sm:px-6 py-4 font-medium text-[#102040]">
                        {admin.firstName || "-"}
                      </td>
                      <td className="px-3 sm:px-6 py-4 font-medium text-[#102040]">
                        {admin.lastName || "-"}
                      </td>
                      <td className="px-3 sm:px-6 py-4 text-[#989797] font-medium">
                        {admin.email}
                      </td>
                       <td className="px-3 sm:px-6 py-4">
                         {admin.isEmailVerified ? (
                           <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#E8F5EE] text-[#1B8754]">
                             <CheckCircle2 size={12} className="mr-1" />
                             verified
                           </span>
                         ) : (
                           <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#FEECEC] text-[#FF4D49]">
                             <XCircle size={12} className="mr-1" />
                             not verified
                           </span>
                         )}
                       </td>
                       <td className="px-3 sm:px-6 py-4">
                         {admin.isSuperAdmin ? (
                           <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                             <Shield size={10} className="mr-1" />
                             Super Admin
                           </span>
                         ) : (
                           <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                             Regular Admin
                           </span>
                         )}
                       </td>
                       <td className="px-3 sm:px-6 py-4">
                         {admin.isSuperAdmin ? (
                           <span className="text-sm font-medium text-gray-600">
                             All Permissions
                           </span>
                         ) : (
                           <span className="text-sm font-medium text-gray-600">
                             {admin.permissions?.length || 0} Permissions
                           </span>
                         )}
                       </td>
                       <td className="px-3 sm:px-6 py-4 text-[#989797]">
                         {formatDate(admin.createdAt)}
                       </td>
                       <td className="px-3 sm:px-6 py-4 text-[#989797]">
                         {formatDate(admin.updatedAt)}
                       </td>
                      <td className="px-3 sm:px-6 py-4 text-right relative">
                        <button
                          className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (activeMenu?.id === admin._id) {
                              setActiveMenu(null);
                            } else {
                              const rect =
                                e.currentTarget.getBoundingClientRect();
                              setActiveMenu({
                                id: admin._id,
                                position: {
                                  top: rect.bottom,
                                  right: window.innerWidth - rect.right,
                                },
                              });
                            }
                          }}
                        >
                          <MoreHorizontal size={20} />
                        </button>

                        <TableActionMenu
                          isOpen={activeMenu?.id === admin._id}
                          onClose={() => setActiveMenu(null)}
                          position={activeMenu?.position || null}
                        >
                          {hasPermission('admin:update-permissions') && (
                            <button
                              onClick={() => {
                                onEditClick(admin);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                            >
                              <Edit2 size={14} className="text-blue-500" />
                              Edit Admin
                            </button>
                          )}
                          {hasPermission('admin:delete') && (
                            <button
                              onClick={() => {
                                onDeleteClick(admin);
                                setActiveMenu(null);
                              }}
                              className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                            >
                              <Trash2 size={14} />
                              Delete Admin
                            </button>
                          )}
                        </TableActionMenu>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2 text-sm text-gray-500">
            <span>
              {statusFilter !== "all" || searchTerm
                ? "Filtered results: "
                : "Showing "}
              {filteredAndSortedAdmins.length} of {admins.length} admins
            </span>
            <div className="flex gap-1 ml-4">
              <button
                className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
                disabled
              >
                &lt; Previous
              </button>
              <button className="px-3 py-1 bg-gray-900 text-white rounded">
                1
              </button>
              <button
                className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
                disabled
              >
                Next &gt;
              </button>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}
